/*
    Copyright © DokiApp, 2026
*/

import React, { useEffect, useState } from 'react'
import {
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import { navigate } from '@reach/router'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { useStoreActions, useStoreState } from '../../store/store.hooks'
import { Heading } from '../../components/Text'
import { NavigateBack } from '../../components/Nav/NavigateBack'
import { GradientButton, WhiteButton } from '../../components/Button'
import { get } from '../../services/request'
import { useRelativeNavigate } from '../../hooks/useRelativeNavigate'
import { MYMEDIO_URLS } from '../../constants'
import { EvaluationBandBar } from './EvaluationBandBar'

const BFI_RANK_COLORS = {
  1: '#AEDD94',
  2: '#CBE8BA',
  3: '#F4EB49',
  4: '#FF5D55',
  5: '#FF3823',
}

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  bookingButton: {
    width: '90%',
  },
  bottomButton: {
    width: '90%',
    border: '1px solid #6473AF',
    color: '#1E2864',
  },
  buttonContainer: {
    margin: '0 2rem 2rem',
  },
  centered: {
    textAlign: 'center',
  },
  disclaimer: {
    margin: '0 2rem 2rem',
    fontSize: '1.1rem',
    textAlign: 'justify',
  },
  evaluationDescription: {
    fontSize: '1.1rem',
    fontWeight: '600',
    textAlign: 'center',
    color: '#1E2864',
  },
  evaluationDescriptionBox: {
    margin: '0 2rem 1rem',
    padding: '1rem',
    border: '1px solid #1E2864',
    borderRadius: '0.5rem',
  },
  infoText: {
    margin: '0 2rem 2rem',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '1rem',
    color: '#1E2864',
  },
  missingButton: {
    width: 'auto',
    padding: '6px 2rem',
    border: '1px solid #6473AF',
    color: '#1E2864',
    marginTop: '1rem',
  },
  missingText: {
    textAlign: 'center',
    fontSize: '1.1rem',
    color: '#1E2864',
  },
  page: {
    backgroundColor: '#fff',
    borderRadius: '15px',
  },
  pageHeader: {
    marginBottom: theme.spacing(3),
  },
  section: {
    border: '2px solid #1E2864',
    borderRadius: '12px',
    padding: '1rem 2rem',
    margin: '0 2rem 2rem',
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      padding: '1rem',
      margin: '0 1rem 1.5rem',
    },
  },
  sectionIntro: {
    textAlign: 'center',
    marginBottom: '1rem',
    color: '#1E2864',
  },
  sectionTitle: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
    color: '#1E2864',
    marginBottom: '1rem',
    textTransform: 'uppercase',
  },
  table: {
    border: '1px solid #1E2864',
    '& th': {
      backgroundColor: '#F0F0F0',
      color: '#1E2864',
      fontWeight: 'normal',
    },
    '& th, & td': {
      border: '1px solid #1E2864',
      textAlign: 'left',
      verticalAlign: 'middle',
    },
  },
  tableContainer: {
    overflowX: 'auto',
    width: '100%',
  },
}))

const MissingSection = ({ classes, t, relativeNavigate, title, surveyKey }) => (
  <Grid item xs={12}>
    <Box className={classes.section}>
      <Typography className={classes.sectionTitle}>{title}</Typography>
      <Typography className={classes.missingText}>
        {t('mentalWellbeingDaysNotFilledText')}
      </Typography>
      <WhiteButton
        className={classes.missingButton}
        onClick={() => relativeNavigate(`/surveys/${surveyKey}/questions`)}
      >
        {t('mentalWellbeingDaysNotFilledButton')}
      </WhiteButton>
    </Box>
  </Grid>
)

const MentalWellbeingDaysEvaluation = () => {
  const classes = useStyles()
  const {
    t,
    i18n: { language },
  } = useTranslation('survey')
  const { relativeNavigate } = useRelativeNavigate()
  const { fetchSettings } = useStoreActions((actions) => actions.settings)
  const { settingsByKey } = useStoreState((state) => state.settings)

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchEvaluation = async () => {
      try {
        setLoading(true)
        const result = await get(
          `/surveys/4ig-mental-wellbeing-days/evaluation?lng=${language}`
        )
        setData(result)
      } catch (err) {
        console.error('Failed to fetch mental wellbeing days evaluation', err)
      } finally {
        setLoading(false)
      }
    }

    fetchEvaluation()
  }, [])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  const handleBookingClick = () => {
    const url = settingsByKey[MYMEDIO_URLS.MENTAL_WELLBEING_DAYS_BOOKING]
    if (!url) return

    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
  }

  const handleBack = () => {
    relativeNavigate('/surveys/4ig')
  }

  const handleBackToServices = () => {
    navigate('/patient/services')
  }

  const formatScore = (score) => score.toFixed(2).replace('.', ',')

  if (loading) {
    return (
      <Backdrop className={classes.backdrop} open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    )
  }

  if (!data) {
    return null
  }

  const { who5, bfi10, tki } = data

  return (
    <Grid container className={classes.page}>
      <NavigateBack onBack={handleBack} />
      <Grid item xs={12} className={classes.pageHeader}>
        <Heading>{t('mentalWellbeingEvalHeader')}</Heading>
      </Grid>

      <Grid item xs={12}>
        <Typography className={classes.disclaimer}>
          {t('mentalWellbeingEvalDisclaimer')}
        </Typography>
      </Grid>

      {who5.hasResponses ? (
        <Grid item xs={12}>
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {t('mentalWellbeingDaysWho5SectionTitle')}
            </Typography>
            <EvaluationBandBar
              evaluations={who5.evaluations}
              evaluation={who5.evaluation}
            />
            <Box className={classes.evaluationDescriptionBox}>
              <Typography className={classes.evaluationDescription}>
                {who5.evaluation?.description}
              </Typography>
            </Box>
          </Box>
        </Grid>
      ) : (
        <MissingSection
          classes={classes}
          t={t}
          relativeNavigate={relativeNavigate}
          title={t('mentalWellbeingDaysWho5SectionTitle')}
          surveyKey="4ig-who-5"
        />
      )}

      {bfi10.hasResponses ? (
        <Grid item xs={12}>
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {t('mentalWellbeingDaysBfi10SectionTitle')}
            </Typography>
            <Typography className={classes.sectionIntro}>
              {t('mentalWellbeingDaysBfi10ResultsIntro')}
            </Typography>
            <Box className={classes.tableContainer}>
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('mentalWellbeingDaysTableScore')}</TableCell>
                    <TableCell>
                      {t('mentalWellbeingDaysTableCategory')}
                    </TableCell>
                    <TableCell>
                      {t('mentalWellbeingDaysTableDimension')}
                    </TableCell>
                    <TableCell>
                      {t('mentalWellbeingDaysTableInterpretation')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bfi10.factors.map((factor) => (
                    <TableRow
                      key={factor.key}
                      style={{ backgroundColor: BFI_RANK_COLORS[factor.rank] }}
                    >
                      <TableCell>{formatScore(factor.score)}</TableCell>
                      <TableCell>
                        {t(`mentalWellbeingDaysRank${factor.rank}`)}
                      </TableCell>
                      <TableCell>
                        {t(
                          `mentalWellbeingDaysBfi${_.upperFirst(
                            factor.key
                          )}Name`
                        )}
                      </TableCell>
                      <TableCell>
                        {t(
                          `mentalWellbeingDaysBfi${_.upperFirst(
                            factor.key
                          )}Desc`
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Grid>
      ) : (
        <MissingSection
          classes={classes}
          t={t}
          relativeNavigate={relativeNavigate}
          title={t('mentalWellbeingDaysBfi10SectionTitle')}
          surveyKey="4ig-bfi10"
        />
      )}

      {tki.hasResponses ? (
        <Grid item xs={12}>
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {t('mentalWellbeingDaysTkiSectionTitle')}
            </Typography>
            <Typography
              className={classes.sectionIntro}
              dangerouslySetInnerHTML={{
                __html: t('mentalWellbeingDaysTkiResultsIntro'),
              }}
            />
            <Box className={classes.tableContainer}>
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('mentalWellbeingDaysTableScore')}</TableCell>
                    <TableCell>{t('mentalWellbeingDaysTableBand')}</TableCell>
                    <TableCell>
                      {t('mentalWellbeingDaysTableStrategy')}
                    </TableCell>
                    <TableCell>
                      {t('mentalWellbeingDaysTableInterpretation')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tki.categories.map((category) => (
                    <TableRow key={category.key}>
                      <TableCell>{category.score}</TableCell>
                      <TableCell>
                        {t(
                          `mentalWellbeingDaysBand${_.upperFirst(
                            category.band
                          )}`
                        )}
                      </TableCell>
                      <TableCell>
                        {t(
                          `mentalWellbeingDaysTki${_.upperFirst(
                            category.key
                          )}Name`
                        )}
                      </TableCell>
                      <TableCell>
                        {t(
                          `mentalWellbeingDaysTki${_.upperFirst(
                            category.key
                          )}Desc`
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Grid>
      ) : (
        <MissingSection
          classes={classes}
          t={t}
          relativeNavigate={relativeNavigate}
          title={t('mentalWellbeingDaysTkiSectionTitle')}
          surveyKey="4ig-tki"
        />
      )}

      <Grid item xs={12}>
        <Typography className={classes.infoText}>
          {t('mentalWellbeingDaysEvalInfoText')}
        </Typography>
      </Grid>

      <Grid
        container
        justifyContent="center"
        spacing={2}
        className={classes.buttonContainer}
      >
        <Grid item xs={6} className={classes.centered}>
          <GradientButton
            className={classes.bookingButton}
            onClick={handleBookingClick}
          >
            {t('mentalWellbeingDaysBookingButton')}
          </GradientButton>
        </Grid>
        <Grid item xs={6} className={classes.centered}>
          <WhiteButton
            className={classes.bottomButton}
            onClick={handleBackToServices}
          >
            {t('mentalWellbeingDaysEvalBackButton')}
          </WhiteButton>
        </Grid>
      </Grid>
    </Grid>
  )
}

MentalWellbeingDaysEvaluation.displayName = 'MentalWellbeingDaysEvaluation'

export default MentalWellbeingDaysEvaluation
export { MentalWellbeingDaysEvaluation }
